const INDIAN_MOBILE_E164 = /^\+91[6-9][0-9]{9}$/;

/**
 * Strip non-digits from a phone input string.
 * @param value - The input string
 * @returns Digits-only string
 */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Returns true when value is a valid Indian mobile in E.164 (+91XXXXXXXXXX).
 * @param value - The phone string to check
 * @returns Whether the value matches Indian mobile E.164 format
 */
export function isValidIndianMobileE164(value: string): boolean {
  return INDIAN_MOBILE_E164.test(value);
}

/**
 * Try to normalize a phone string starting with +91.
 * @param trimmed - The trimmed phone string
 * @returns The normalized number, or null if it doesn't start with +91
 */
function tryNormalizePlus91(trimmed: string): string | null {
  if (!trimmed.startsWith('+91')) return null;
  const local = digitsOnly(trimmed.slice(3));
  if (local.length === 10 && /^[6-9]/.test(local)) {
    return `+91${local}`;
  }
  return trimmed;
}

/**
 * Try to normalize a digits-only phone string by handling common Indian
 * mobile formats (10-digit, 11-digit with 0 prefix, 12-digit with 91 prefix).
 * @param digits - The digits-only phone string
 * @returns The normalized E.164 number, or null if no format matches
 */
function tryNormalizeDigits(digits: string): string | null {
  if (digits.length === 10 && /^[6-9]/.test(digits)) {
    return `+91${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('0') && /^0[6-9]/.test(digits)) {
    return `+91${digits.slice(1)}`;
  }
  if (digits.length === 12 && digits.startsWith('91') && /^91[6-9]/.test(digits)) {
    return `+${digits}`;
  }
  return null;
}

/**
 * Normalize common Indian mobile inputs to E.164 (+91XXXXXXXXXX).
 * Returns the original string when normalization is not possible.
 * @param value - The phone input string
 * @returns Normalized E.164 phone string or original input
 */
export function normalizeIndianPhone(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (INDIAN_MOBILE_E164.test(trimmed)) return trimmed;

  const plus91 = tryNormalizePlus91(trimmed);
  if (plus91) return plus91;

  const digits = digitsOnly(trimmed);
  const normalized = tryNormalizeDigits(digits);
  return normalized ?? trimmed;
}

/**
 * Display E.164 Indian mobile as local 10-digit (for form inputs with +91 prefix).
 * @param e164 - The E.164 phone string
 * @returns The local 10-digit number, or the original string if not a valid Indian mobile
 */
export function stripIndianPhoneForDisplay(e164: string | null | undefined): string {
  if (!e164) return '';
  const match = /^\+91([6-9][0-9]{9})$/.exec(e164.trim());
  return match?.[1] ?? e164;
}

/** @deprecated Use stripIndianPhoneForDisplay */
export const displayIndianPhoneLocal = stripIndianPhoneForDisplay;
